import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Building2, Upload, CheckCircle2, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";

const DOCS = [
  { key: "acta_constitutiva", label: "Acta constitutiva" },
  { key: "poderes", label: "Poderes del representante legal" },
  { key: "id_representante", label: "Identificación del representante legal" },
  { key: "comprobante_domicilio", label: "Comprobante de domicilio" },
];

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ filename: file.name, data_url: r.result });
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function RegisterCompany() {
  const { registerCompany } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    company_name: "", first_name: "", last_name: "", username: "", personal_email: "", password: "",
    legal_rep_name: "", legal_rep_contact: "", rfc: "",
    acta_constitutiva: null, poderes: null, id_representante: null, comprobante_domicilio: null,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setFile = (k) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setError(`El archivo "${file.name}" supera 8MB.`); return; }
    setError("");
    const doc = await fileToDataUrl(file);
    setForm((f) => ({ ...f, [k]: doc }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await registerCompany(form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const uname = (form.username.trim().toLowerCase().split("@")[0] || "usuario");
  const inputCls = "mt-2 w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-cream">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center" data-testid="register-company-success">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-6" />
          <h2 className="text-3xl font-medium tracking-tight mb-3">Solicitud enviada</h2>
          <p className="text-navy/60 leading-relaxed">
            Recibimos la solicitud de alta de <b>{form.company_name}</b>. Un administrador de OMNIA
            revisará tus documentos y aprobará el acceso. Te notificaremos a <b>{form.personal_email}</b>.
          </p>
          <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-violet hover:underline" data-testid="rc-success-login">
            Ir a iniciar sesión <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-cream">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-lg">
        <Link to="/" className="font-sans text-xl tracking-[0.25em] block mb-8">OMNIA</Link>

        <div className="overline text-navy/40 mb-3 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> {t("auth.createSpace")}</div>
        <h2 className="text-3xl font-medium tracking-tight mb-2">Solicitar alta de empresa</h2>
        <p className="text-sm text-navy/50 mb-8">Comparte los datos y documentos de tu empresa. Un administrador de OMNIA los revisará y aprobará el acceso.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="register-company-form">
          <div>
            <label className="overline text-navy/50">{t("auth.companyName")}</label>
            <input data-testid="rc-company" value={form.company_name} onChange={set("company_name")} required minLength={2} placeholder="Mi Empresa S.A. de C.V." className={inputCls} />
          </div>

          <div className="pt-2 overline text-navy/40">Administrador de la cuenta</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline text-navy/50">{t("auth.firstName")}</label>
              <input data-testid="rc-first-name" value={form.first_name} onChange={set("first_name")} required className={inputCls} />
            </div>
            <div>
              <label className="overline text-navy/50">{t("auth.lastName")}</label>
              <input data-testid="rc-last-name" value={form.last_name} onChange={set("last_name")} required className={inputCls} />
            </div>
          </div>
          <div>
            <label className="overline text-navy/50">{t("auth.user")}</label>
            <div className="mt-2 flex items-center bg-white border border-navy/10 rounded-lg px-4 focus-within:border-violet transition-colors">
              <input data-testid="rc-username" type="text" value={form.username} onChange={set("username")} required autoCapitalize="none" className="flex-1 bg-transparent py-3 outline-none" placeholder="oscar" />
              <span className="text-navy/40 select-none">@omnia.os</span>
            </div>
            <p className="mt-1.5 text-xs text-navy/40">{t("auth.yourAccessWillBe")} <b>{uname}@omnia.os</b></p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline text-navy/50">{t("auth.personalEmail")}</label>
              <input data-testid="rc-personal-email" type="email" value={form.personal_email} onChange={set("personal_email")} required autoCapitalize="none" placeholder="tucorreo@gmail.com" className={inputCls} />
            </div>
            <div>
              <label className="overline text-navy/50">{t("auth.password")}</label>
              <input data-testid="rc-password" type="password" value={form.password} onChange={set("password")} required minLength={6} className={inputCls} />
            </div>
          </div>

          <div className="pt-2 overline text-navy/40">Datos legales</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline text-navy/50">Responsable legal</label>
              <input data-testid="rc-legal-rep" value={form.legal_rep_name} onChange={set("legal_rep_name")} required placeholder="Nombre completo" className={inputCls} />
            </div>
            <div>
              <label className="overline text-navy/50">Contacto del responsable</label>
              <input data-testid="rc-legal-contact" value={form.legal_rep_contact} onChange={set("legal_rep_contact")} required placeholder="Correo o teléfono" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="overline text-navy/50">RFC</label>
            <input data-testid="rc-rfc" value={form.rfc} onChange={set("rfc")} required placeholder="XAXX010101000" className={inputCls} />
          </div>

          <div className="pt-2 overline text-navy/40">Documentos</div>
          <div className="space-y-3">
            {DOCS.map((d) => (
              <label key={d.key} data-testid={`rc-doc-${d.key}`}
                className="flex items-center gap-3 bg-white border border-navy/10 rounded-lg px-4 py-3 cursor-pointer hover:border-violet transition-colors">
                {form[d.key] ? <FileText className="w-4 h-4 text-emerald-500 shrink-0" /> : <Upload className="w-4 h-4 text-navy/40 shrink-0" />}
                <span className="text-sm text-navy/70 flex-1 truncate">{form[d.key] ? form[d.key].filename : d.label}</span>
                <span className="text-xs text-violet">{form[d.key] ? "Cambiar" : "Subir"}</span>
                <input type="file" className="hidden" onChange={setFile(d.key)} accept=".pdf,.jpg,.jpeg,.png,.webp" />
              </label>
            ))}
            <p className="text-xs text-navy/40">PDF o imagen, máx. 8MB por archivo.</p>
          </div>

          {error && <p data-testid="register-company-error" className="text-sm text-destructive">{error}</p>}

          <button data-testid="register-company-submit" disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar solicitud"}
            {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
          </button>
        </form>

        <p className="mt-6 text-sm text-navy/50">
          {t("auth.alreadyAccount")}{" "}
          <Link to="/login" data-testid="link-login" className="text-violet hover:underline">{t("auth.signIn")}</Link>
        </p>
      </motion.div>
    </div>
  );
}
